import type { Fetcher } from "../fetcher";
import type { ApiCollectionResponse, ApiSingleResponse } from "../types/api";
import type {
  CreateProformaDto,
  InvoiceDTO,
  InvoiceDownloadTarget,
  InvoiceListStats,
  ListInvoicesParams,
  UpdateInvoiceDto,
} from "./types";

const EMPTY_STATS: InvoiceListStats = {
  issuedTotal: 0,
  issuedCount: 0,
  draftCount: 0,
  allocatedTotal: 0,
  outstandingTotal: 0,
};

export function createInvoicesRepository(fetcher: Fetcher) {
  return {
    async list(params: ListInvoicesParams = {}): Promise<{
      items: InvoiceDTO[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
      stats: InvoiceListStats;
    }> {
      const qs = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) qs.append(key, String(value));
      }
      const query = qs.toString();
      const res = await fetcher<
        ApiCollectionResponse<InvoiceDTO, { stats?: InvoiceListStats }>
      >(`/invoices${query ? `?${query}` : ""}`);
      const payload = res.data ?? ({} as Partial<{
        items: InvoiceDTO[];
        pagination: { page: number; limit: number; total: number; totalPages: number };
        stats: InvoiceListStats;
      }>);
      // `ApiCollectionResponse`'s pagination type omits `totalPages`, which the
      // API does send — widen it here rather than loosening the shared type.
      const pagination = (payload.pagination ?? {}) as Partial<{
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      }>;
      return {
        items: payload.items ?? [],
        pagination: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 20,
          total: pagination.total ?? 0,
          totalPages: pagination.totalPages ?? 1,
        },
        stats: payload.stats ?? EMPTY_STATS,
      };
    },

    async get(id: string): Promise<InvoiceDTO> {
      const res = await fetcher<ApiSingleResponse<InvoiceDTO>>(`/invoices/${id}`);
      return res.data;
    },

    /** Creates a draft. Nothing is numbered or sent until `issue`. */
    async createProforma(payload: CreateProformaDto): Promise<InvoiceDTO> {
      const res = await fetcher<ApiSingleResponse<InvoiceDTO>>(
        "/invoices/proforma",
        { method: "POST", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    /** Drafts only — the server rejects an edit to an issued document. */
    async update(id: string, payload: UpdateInvoiceDto): Promise<InvoiceDTO> {
      const res = await fetcher<ApiSingleResponse<InvoiceDTO>>(
        `/invoices/${id}`,
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      return res.data;
    },

    /** Allocates the number, freezes the amounts, renders the PDF. */
    async issue(id: string, issueDate?: string): Promise<InvoiceDTO> {
      const res = await fetcher<ApiSingleResponse<InvoiceDTO>>(
        `/invoices/${id}/issue`,
        { method: "POST", body: JSON.stringify(issueDate ? { issueDate } : {}) },
      );
      return res.data;
    },

    async voidInvoice(id: string, reason: string): Promise<InvoiceDTO> {
      const res = await fetcher<ApiSingleResponse<InvoiceDTO>>(
        `/invoices/${id}/void`,
        { method: "POST", body: JSON.stringify({ reason }) },
      );
      return res.data;
    },

    async downloadUrl(
      id: string,
      disposition?: "attachment" | "inline",
    ): Promise<InvoiceDownloadTarget> {
      const qs = disposition ? `?disposition=${disposition}` : "";
      const res = await fetcher<ApiSingleResponse<InvoiceDownloadTarget>>(
        `/invoices/${id}/download${qs}`,
      );
      return res.data;
    },

    /** Drafts only. An issued invoice is voided, never deleted. */
    async delete(id: string): Promise<void> {
      await fetcher(`/invoices/${id}`, { method: "DELETE" });
    },
  };
}
